import { useCallback, useMemo, useState } from 'react';
import { useResettableState } from 'hooks/useResettableState';
import { useNavigate } from 'react-router-dom';
import { InspectionTemplateItemModel } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import { useAppNavigation } from 'contexts/navigation';
import { usePermissions } from 'hooks/permissions/usePermissions';
import { useSelectNextItem } from 'hooks/useSelectNextItem';
import { useProject } from 'store/project';
import { InspectionTemplateStatus } from 'shared/domain/template/templateStatus';
import { toInspectionOutDto, InspectionSubmitFormFields } from 'shared/domain/inspection/mapping/toOutDto';
import { showErrorToastAndLog } from 'services/toaster';
import {
  useInspectionStore,
  useInspectionTemplateListItems,
  useTemplateActions,
} from 'store/inspection';
import { APP_PATHS } from 'setup/navigation/paths';
import { NOT_FOUND_ERROR } from '../helpers';
import {
  useTemplateListMobileView,
  useTemplateListSearch,
  useTemplateListSelection,
} from '../hooks';
import { TemplateListPresentationalProps } from '../types';

export interface TemplateListControllerResult {
  showSkeleton: boolean;
  showNotFound: boolean;
  startInspectionDialogOpen: boolean;
  startInspectionDialogTemplate: InspectionTemplateItemModel | undefined;
  closeStartInspectionDialog: () => void;
  submitStartInspection: (values: InspectionSubmitFormFields) => Promise<void>;
  startInspectionSubmitting: boolean;
  presentationalProps: TemplateListPresentationalProps;
}

export function useTemplateListController(): TemplateListControllerResult {
  const navigate = useNavigate();
  const project = useProject();
  const {
    goToInspectionTemplateEdit,
    goToInspectionDetails,
    goToCreateInspectionTemplate,
  } = useAppNavigation();
  const {
    canCreateInspectionTemplate,
    canEditInspectionTemplate,
    canDeleteInspectionTemplate,
    canCreateInspection,
  } = usePermissions();

  const { items, loading } = useInspectionTemplateListItems();
  const postInspection = useInspectionStore.use.postInspection();
  // Hidden only for the list it was clicked in: a reloaded list shows it again.
  const [hideFromUrlItem, setHideFromUrlItem] = useResettableState(items, () => false);
  const [startInspectionDialogTemplate, setStartInspectionDialogTemplate] =
    useState<InspectionTemplateItemModel | undefined>(undefined);
  const [startInspectionSubmitting, setStartInspectionSubmitting] =
    useState(false);

  const listItems = useMemo(() => {
    const [firstItem] = items;
    if (hideFromUrlItem && firstItem?.fromUrl) {
      return items.slice(1);
    }
    return items;
  }, [items, hideFromUrlItem]);

  const {
    filteredItems,
    searchPhrase,
    searchChange,
    searchClear,
    debouncedSearchPhrase,
  } = useTemplateListSearch(listItems);

  const {
    active,
    activeRef,
    setActive,
    error,
    setCanScroll,
    selectTemplate,
    loadingLastSelected,
  } = useTemplateListSelection({
    items: listItems,
    filteredItems,
    loading,
    searchPhrase,
    debouncedSearchPhrase,
  });

  const handleNoTemplatesLeft = useCallback(() => {
    navigate(APP_PATHS.inspectionTemplate.root(project._id), { replace: true });
  }, [navigate, project._id]);

  const handleSelectTemplate = useCallback(
    (template: InspectionTemplateItemModel) => {
      selectTemplate(template);
    },
    [selectTemplate]
  );

  const { selectNextItem: selectNextTemplate } = useSelectNextItem({
    items: listItems,
    setActive,
    onNoItemsLeft: handleNoTemplatesLeft,
    onSelectItem: handleSelectTemplate,
  });

  const {
    archiveTemplate,
    restoreTemplate,
    removeTemplate: removeTemplateAction,
    isRemoving,
  } = useTemplateActions({
    onAfterArchive: selectNextTemplate,
    onAfterRestore: selectNextTemplate,
    onAfterRemove: selectNextTemplate,
  });

  const { setObserverOn, showList, setShowList } = useTemplateListMobileView({
    active,
    activeRef,
    searchPhrase,
    loading: loading || loadingLastSelected,
  });

  const handleClick = useCallback(
    (template: InspectionTemplateItemModel): void => {
      setShowList(false);
      setObserverOn();

      if (active === template._id) {
        return;
      }

      selectTemplate(template);

      if (listItems[0]?.fromUrl) {
        setHideFromUrlItem(true);
      }
    },
    [active, listItems, selectTemplate, setObserverOn, setShowList]
  );

  const canEditTemplate = useCallback(
    (template: InspectionTemplateItemModel) => {
      const isPublished =
        template.status === InspectionTemplateStatus.published;
      return canEditInspectionTemplate(isPublished, template.deleted);
    },
    [canEditInspectionTemplate]
  );

  const editTemplate = useCallback(
    (template: InspectionTemplateItemModel): void => {
      goToInspectionTemplateEdit(template._id);
    },
    [goToInspectionTemplateEdit]
  );

  const canRemoveTemplate = useCallback(() => {
    return canDeleteInspectionTemplate();
  }, [canDeleteInspectionTemplate]);

  const removeTemplate = useCallback(
    (templateId: string, templateSummary: string): void => {
      if (!canRemoveTemplate().permission) {
        return;
      }

      void removeTemplateAction(templateId, templateSummary);
    },
    [canRemoveTemplate, removeTemplateAction]
  );

  const canCreateTemplate = useCallback(() => {
    return canCreateInspectionTemplate();
  }, [canCreateInspectionTemplate]);

  const canStartInspection = useCallback(
    (template: InspectionTemplateItemModel) => {
      const isPublished =
        template.status === InspectionTemplateStatus.published;
      return canCreateInspection(isPublished, template.deleted);
    },
    [canCreateInspection]
  );

  const startInspection = useCallback(
    (template: InspectionTemplateItemModel): void => {
      setStartInspectionDialogTemplate(template);
    },
    []
  );

  const closeStartInspectionDialog = useCallback(() => {
    if (startInspectionSubmitting) {
      return;
    }
    setStartInspectionDialogTemplate(undefined);
  }, [startInspectionSubmitting]);

  const submitStartInspection = useCallback(
    async (values: InspectionSubmitFormFields): Promise<void> => {
      if (!startInspectionDialogTemplate) {
        return;
      }

      setStartInspectionSubmitting(true);

      try {
        const inspectionOutDto = toInspectionOutDto(undefined, values);
        const createdInspection = await postInspection(
          startInspectionDialogTemplate._id,
          inspectionOutDto,
          project._id
        );

        setStartInspectionDialogTemplate(undefined);
        goToInspectionDetails(project._id, createdInspection._id);
      } catch (error: unknown) {
        showErrorToastAndLog(
          'Error while creating inspection',
          error
        );
      } finally {
        setStartInspectionSubmitting(false);
      }
    },
    [goToInspectionDetails, postInspection, project._id, startInspectionDialogTemplate]
  );

  const showSkeleton = loading || loadingLastSelected || isRemoving;
  const showNotFound = error === NOT_FOUND_ERROR;
  const loaded = !loading && !loadingLastSelected;

  return {
    showSkeleton,
    showNotFound,
    startInspectionDialogOpen: Boolean(startInspectionDialogTemplate),
    startInspectionDialogTemplate,
    closeStartInspectionDialog,
    submitStartInspection,
    startInspectionSubmitting,
    presentationalProps: {
      templates: filteredItems,
      activeId: active,
      showList,
      handleClick,
      error,
      loaded,
      canCreateTemplate,
      createTemplate: goToCreateInspectionTemplate,
      canEditTemplate,
      editTemplate,
      canRemoveTemplate,
      removeTemplate,
      archiveTemplate,
      restoreTemplate,
      setCanScroll,
      searchChange,
      searchClear,
      searchPhrase,
      startInspection,
      canStartInspection,
    },
  };
}
